import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'common/store';
import {
  ONBOARDING_EMAIL_TEMPLATES,
  RENEWAL_TEMPLATE_ID,
} from 'dashboard409a/components/OnboardingEmailModal/OnboardingEmailModal.templates';
import type { OnboardingEmailTemplate } from 'dashboard409a/components/OnboardingEmailModal/OnboardingEmailModal.types';
import useEmailTemplatePresets from 'dashboard409a/hooks/useEmailTemplatePresets';
import {
  deleteWorkboardEmailTemplatePreset,
  fromWorkboardEmailTemplate,
  getWorkboardEmailTemplatesQueryKey,
  migrateLocalEmailTemplatePresetsIfNeeded,
  saveWorkboardEmailTemplatePreset,
} from 'dashboard409a/hooks/workboardEmailTemplateStorage';
import {
  ONBOARDING_EMAIL_TEMPLATE_KEY,
  ONBOARDING_EMAIL_TEMPLATE_PRESETS_KEY,
  ONBOARDING_EMAIL_TEMPLATES_MIGRATED_KEY,
} from 'dashboard409a/pages/Workboard/common/constants';
import {
  listWorkboardEmailTemplates,
  ONBOARDING_EMAIL_TEMPLATE_KIND,
  RENEWAL_EMAIL_TEMPLATE_KIND,
  type WorkboardEmailTemplateKind,
} from 'dashboard409a/services/workboardEmailTemplates';

const pickOnboardingPresetFields = (template: OnboardingEmailTemplate): OnboardingEmailTemplate => ({
  id: template.id,
  label: template.label,
  emailSubject: template.emailSubject,
  introduction: template.introduction,
  documents: template.documents?.length
    ? template.documents
    : (ONBOARDING_EMAIL_TEMPLATES[0]?.documents ?? []),
});

const documentsForTemplateId = (templateId: string) =>
  ONBOARDING_EMAIL_TEMPLATES.find(template => template.id === templateId)?.documents
  ?? ONBOARDING_EMAIL_TEMPLATES[0]?.documents
  ?? [];

const toPresetTemplate =
  (templateId: string) =>
    (row: { email_subject: string; introduction: string }): OnboardingEmailTemplate => ({
      id: `preset-server-${row.email_subject}`,
      label: '',
      emailSubject: row.email_subject,
      introduction: row.introduction,
      documents: documentsForTemplateId(templateId),
    });

export const useOnboardingEmailTemplates = () => {
  const [{ user }] = useStore();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const onboardingQueryKey = getWorkboardEmailTemplatesQueryKey(userId, ONBOARDING_EMAIL_TEMPLATE_KIND);
  const renewalQueryKey = getWorkboardEmailTemplatesQueryKey(userId, RENEWAL_EMAIL_TEMPLATE_KIND);
  const defaults = useMemo(() => ONBOARDING_EMAIL_TEMPLATES, []);

  const { data: onboardingPresets = [], isLoading } = useQuery({
    queryKey: onboardingQueryKey,
    queryFn: async () => {
      await migrateLocalEmailTemplatePresetsIfNeeded({
        userId: userId as number,
        kind: ONBOARDING_EMAIL_TEMPLATE_KIND,
        presetsKey: ONBOARDING_EMAIL_TEMPLATE_PRESETS_KEY,
        migrationKey: ONBOARDING_EMAIL_TEMPLATES_MIGRATED_KEY,
      });
      const response = await listWorkboardEmailTemplates(ONBOARDING_EMAIL_TEMPLATE_KIND);
      return response.definitions.map(row =>
        fromWorkboardEmailTemplate(row, toPresetTemplate(defaults[0]?.id ?? ''))
      );
    },
    enabled: userId !== undefined,
    staleTime: 30_000,
  });

  const { data: renewalPresets = [] } = useQuery({
    queryKey: renewalQueryKey,
    queryFn: async () => {
      const response = await listWorkboardEmailTemplates(RENEWAL_EMAIL_TEMPLATE_KIND);
      return response.definitions.map(row =>
        fromWorkboardEmailTemplate(row, toPresetTemplate(RENEWAL_TEMPLATE_ID))
      );
    },
    enabled: userId !== undefined,
    staleTime: 30_000,
  });

  // Onboarding and renewal share one picker, so presets from both kinds are
  // listed together and saves are routed back to the kind in use.
  const serverPresets = useMemo(
    () => [...onboardingPresets, ...renewalPresets],
    [onboardingPresets, renewalPresets]
  );

  const presetKindByName = useMemo(() => {
    const map = new Map<string, WorkboardEmailTemplateKind>();
    onboardingPresets.forEach(preset => map.set(preset.name, ONBOARDING_EMAIL_TEMPLATE_KIND));
    renewalPresets.forEach(preset => map.set(preset.name, RENEWAL_EMAIL_TEMPLATE_KIND));
    return map;
  }, [onboardingPresets, renewalPresets]);

  const selectedIdRef = useRef('');

  const resolveKindForPreset = useCallback(
    (name: string): WorkboardEmailTemplateKind => {
      const existingKind = presetKindByName.get(name);
      if (existingKind) {
        return existingKind;
      }
      const selectedId = selectedIdRef.current;
      if (selectedId.startsWith('preset:')) {
        return (
          presetKindByName.get(selectedId.slice('preset:'.length))
          ?? ONBOARDING_EMAIL_TEMPLATE_KIND
        );
      }
      return selectedId === RENEWAL_TEMPLATE_ID
        ? RENEWAL_EMAIL_TEMPLATE_KIND
        : ONBOARDING_EMAIL_TEMPLATE_KIND;
    },
    [presetKindByName]
  );

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: onboardingQueryKey }),
      queryClient.invalidateQueries({ queryKey: renewalQueryKey }),
    ]);
  }, [queryClient, onboardingQueryKey, renewalQueryKey]);

  const { mutateAsync: savePresetMutation } = useMutation({
    mutationFn: ({
      name,
      template,
      existingId,
    }: {
      name: string;
      template: OnboardingEmailTemplate;
      existingId?: number;
    }) =>
      saveWorkboardEmailTemplatePreset({
        kind: resolveKindForPreset(name),
        existingId,
        name,
        template,
      }),
    onSuccess: () => invalidate(),
  });

  const { mutateAsync: deletePresetMutation } = useMutation({
    mutationFn: (presetId: number) => deleteWorkboardEmailTemplatePreset(presetId),
    onSuccess: () => invalidate(),
  });

  const onSavePreset = useCallback(
    async (name: string, template: OnboardingEmailTemplate, existingId?: number) => {
      await savePresetMutation({ name, template, existingId });
    },
    [savePresetMutation]
  );

  const onDeletePreset = useCallback(
    async (_name: string, presetId: number) => {
      await deletePresetMutation(presetId);
    },
    [deletePresetMutation]
  );

  const onUpdatePreset = useCallback(
    async (name: string, template: OnboardingEmailTemplate, presetId: number) => {
      await savePresetMutation({ name, template, existingId: presetId });
    },
    [savePresetMutation]
  );

  const {
    templates,
    presets,
    selectedId,
    selectedTemplate: rawSelectedTemplate,
    setSelectedId,
    updateSelectedTemplate,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useEmailTemplatePresets<OnboardingEmailTemplate>({
    activeKey: ONBOARDING_EMAIL_TEMPLATE_KEY,
    defaults,
    pickPresetFields: pickOnboardingPresetFields,
    presets: serverPresets,
    onSavePreset,
    onDeletePreset,
    onUpdatePreset,
  });

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const selectedTemplate = useMemo(() => {
    const selected = rawSelectedTemplate;
    if (!selected) {
      return defaults[0] ?? null;
    }
    const documents =
      defaults.find(item => item.id === selected.id)?.documents ??
      (selected.documents?.length ? selected.documents : defaults[0]?.documents) ??
      [];
    return { ...selected, documents };
  }, [rawSelectedTemplate, defaults]);

  const setEmailSubject = useCallback(
    (emailSubject: string) => {
      updateSelectedTemplate({ emailSubject });
    },
    [updateSelectedTemplate]
  );

  const setIntroduction = useCallback(
    (introduction: string) => {
      updateSelectedTemplate({ introduction });
    },
    [updateSelectedTemplate]
  );

  return {
    templates,
    presets,
    selectedId,
    selectedTemplate,
    setSelectedId,
    updateSelectedTemplate,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    setEmailSubject,
    setIntroduction,
    isLoadingPresets: isLoading,
  };
};

export default useOnboardingEmailTemplates;
